import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { SiteConfigManager } from '@atlas/core';
import { casinoRoutes, clientProfileRoutes, COMPONENT_TYPES, paymentRoutes } from '@atlas/routing';

import { isBonusTemplate } from '../shared/bonusFeedUtils';
import { buildCasinoGamePath, getFreespinGameId } from '../shared/casinoGameUrl';
import { buildFreeSpinDepositState } from '../shared/freeSpinDepositState';

import { useBonusDetailContext } from './BonusDetailContext';
import { BONUS_KIND } from './resolveBonusEntity';

const { CASINO, CLIENT_PROFILE } = COMPONENT_TYPES;
const { payment } = clientProfileRoutes;
const { deposit } = paymentRoutes;
const casinoGamePath = casinoRoutes.game;

export const useBonusActions = () => {
  const { actions, kind, row } = useBonusDetailContext();
  const history = useHistory();

  return useMemo(() => {
    const clientProfilePath = SiteConfigManager.getComponentPath(CLIENT_PROFILE);
    const casinoPath = SiteConfigManager.getComponentPath(CASINO);
    const depositPath = `${clientProfilePath}/${payment}/${deposit}`;

    const openDeposit = () => {
      if (!row) return;

      if (kind === BONUS_KIND.FREESPIN) {
        history.push({ pathname: depositPath, state: buildFreeSpinDepositState(row) });
        return;
      }

      const isInstance = !isBonusTemplate(row);
      const templateId = isInstance ? row.bonusTemplateId : row.id;
      const instanceId = isInstance ? row.id : null;

      history.push({
        pathname: depositPath,
        state: {
          preselectBonus: {
            bonusId: templateId ?? null,
            bonusInstanceId: instanceId,
          },
        },
      });
    };

    const activate = () =>
      kind === BONUS_KIND.FREESPIN
        ? actions?.activateFreespin?.(row?.id)
        : actions?.activateBonus?.(row);

    // Freespin Play opens the eligible game itself; fall back to the casino landing
    // page when no game is configured (or for non-freespin bonuses).
    const play = () => {
      const gameId = kind === BONUS_KIND.FREESPIN ? getFreespinGameId(row) : undefined;
      history.push(gameId ? buildCasinoGamePath(casinoPath, casinoGamePath, gameId) : casinoPath);
    };

    return {
      activate,
      // No-deposit freespin: activate then jump straight to the eligible game (single CTA),
      // reusing play so the destination matches the Activated-state Play button.
      activateAndPlay: async () => {
        await activate();
        play();
      },
      cancel: () =>
        kind === BONUS_KIND.FREESPIN
          ? actions?.cancelFreespin?.(row?.id)
          : actions?.deactivateBonus?.(row),
      deposit: openDeposit,
      play,
      remove: () => actions?.deactivateBonus?.(row),
    };
  }, [actions, history, kind, row]);
};
